import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Tag } from "primereact/tag";
import { Image } from "primereact/image";
import { classNames } from "primereact/utils";
import GalleryTemplate from "../../../components/ArticleTemplate/GalleryTemplate";
import "./style.css";

const FeatureDetailsPage = ({
  shopDetails,
  featureId,
  item,
  onClose,
  articles,
}) => {
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleDialogVisible, setArticleDialogVisible] = useState(false);
  const [featureDetails, setFeatureDetails] = useState(item);
  const [loading, setLoading] = useState(!item);
  const [error, setError] = useState(null);

  useEffect(() => {
    // If we have the item data from props, use it directly
    if (item) {
      setFeatureDetails(item);
      setLoading(false);
      return;
    }

    // If no item data, fetch from shopDetails
    const fetchFeatureDetails = () => {
      try {
        if (!shopDetails?.shopType) {
          setError("Shop details not available");
          return;
        }

        // Find the feature in shop details
        const feature = Object.entries(shopDetails.shopType).reduce(
          (found, [category, items]) => {
            if (found) return found;
            if (!Array.isArray(items)) return null;

            const item = items.find((item) => {
              const itemKey = Object.keys(item)[0];
              return itemKey === featureId;
            });

            return item
              ? {
                  ...item[Object.keys(item)[0]],
                  category,
                  type: Object.keys(item)[0],
                }
              : null;
          },
          null
        );

        if (feature) {
          setFeatureDetails(feature);
        } else {
          setError("Feature not found");
        }
      } catch (error) {
        console.error("Error processing feature details:", error);
        setError("Error loading feature details");
      } finally {
        setLoading(false);
      }
    };

    if (shopDetails) {
      fetchFeatureDetails();
    }
  }, [shopDetails, featureId, item]);

  if (loading) {
    return (
      <div className="flex align-items-center justify-content-center h-full">
        <div className="text-center p-4">
          <i className="pi pi-spin pi-spinner text-4xl text-primary-500 mb-3"></i>
          <p className="text-600">Loading feature details...</p>
        </div>
      </div>
    );
  }

  if (error || !featureDetails) {
    return (
      <div className="flex align-items-center justify-content-center h-full">
        <Card className="w-11 md:w-6 lg:w-4">
          <div className="text-center">
            <i className="pi pi-exclamation-triangle text-6xl text-red-500 mb-3"></i>
            <h3 className="mb-3">Unable to Load Details</h3>
            <p className="text-600 mb-4">
              {error || "Feature details not available."}
            </p>
            <Button
              label="Close"
              onClick={onClose}
              className="p-button-outlined"
              icon="pi pi-arrow-left"
              iconPos="left"
            />
          </div>
        </Card>
      </div>
    );
  }

  // Format camelCase to Title Case
  const formatKeyToLabel = (key) => {
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase())
      .replace(/_/g, " ")
      .replace(/\b([A-Z]+)([A-Z])([a-z])/g, "$1 $2$3")
      .trim();
  };

  // Format the feature title
  const featureTitle = formatKeyToLabel(
    featureDetails.subCategory || "feature_details"
  );

  // Format business hours with special cases
  const formatBusinessHours = (hours) => {
    if (!hours?.days) return null;

    // Define the correct day order starting from Monday
    const dayOrder = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];

    // Create a map of the provided days for easy lookup
    const daysMap = {};
    let hasOpenDay = false;

    hours.days.forEach((day) => {
      const dayKey = (day.dayName || day.day || "").toLowerCase();
      if (dayKey) {
        daysMap[dayKey] = day;
        if (day.open) hasOpenDay = true;
      }
    });

    // If all days are closed, return null
    if (!hasOpenDay) return null;

    // Generate all days in order, using provided data or defaulting to closed
    return dayOrder.map((dayKey) => {
      const dayData = daysMap[dayKey] || { open: false };
      const dayName = dayKey.charAt(0).toUpperCase() + dayKey.slice(1);

      const hoursText = dayData.open
        ? `${dayData.openingTime || "09:00"} - ${
            dayData.closingTime || "17:00"
          }`
        : "Closed";

      return {
        day: dayName,
        open: dayData.open,
        hours: hoursText,
        hasBreakTime: hours.hasBreakTime,
        breakTime: {
          start: hours.breakStart,
          end: hours.breakEnd,
        },
        hasSpecialHours: hours.hasSpecialHours,
        specialHoursDescription: hours.specialHoursDescription,
      };
    });
  };

  // Check if there are special hours or break time
  const hasSpecialHoursDescription =
    featureDetails.businessHours?.hasSpecialHours &&
    featureDetails.businessHours?.specialHoursDescription;

  const hasBreakTime = featureDetails.businessHours?.hasBreakTime;

  // Get the days array with proper day names for reference
  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  // Format payment methods
  const paymentMethods =
    featureDetails.paymentMethods?.map((method) => {
      // Handle string or object format
      if (typeof method === "string") {
        return {
          name: formatKeyToLabel(method),
          type: method,
          icon: getPaymentMethodIcon(method),
        };
      }
      return method;
    }) || [];

  // Get payment URLs and images if they exist
  const paymentUrls = featureDetails.paymentUrls || {};
  const paymentImages = featureDetails.paymentImages || {};

  // Helper function to get payment method icon
  function getPaymentMethodIcon(type) {
    const typeLower = type?.toLowerCase();
    const icons = {
      // Bank and Financial - Using card icon for bank as requested
      bank: "pi pi-credit-card",
      bpi: "pi pi-credit-card",
      bdo: "pi pi-credit-card",
      metrobank: "pi pi-credit-card",
      "china bank": "pi pi-credit-card",
      "security bank": "pi pi-credit-card",

      // Cards
      card: "pi pi-credit-card",
      visa: "pi pi-credit-card",
      mastercard: "pi pi-credit-card",
      amex: "pi pi-credit-card",
      "credit card": "pi pi-credit-card",
      "debit card": "pi pi-credit-card",

      // Digital Wallets
      gcash: "pi pi-phone", // Changed to phone icon as requested
      paymaya: "pi pi-wallet",
      paypal: "pi pi-paypal",
      grabpay: "pi pi-shopping-bag",
      coins: "pi pi-bitcoin",

      // Cash and Others
      cash: "pi pi-money-bill",
      remittance: "pi pi-send",
      "bank transfer": "pi pi-exchange",
      "online banking": "pi pi-globe",

      // Default
      default: "pi pi-credit-card",
    };

    // Try exact match first, then check for partial matches
    return (
      icons[typeLower] ||
      Object.entries(icons).find(([key]) => typeLower?.includes(key))?.[1] ||
      icons.default
    );
  }

  // Get current day index (0-6, where 0 is Sunday)
  const currentDayIndex = new Date().getDay();

  return (
    <div className="feature-details-container">
      {/* Header */}
      <div className="feature-header">
        <Button
          icon="pi pi-arrow-left"
          text
          onClick={onClose}
          className="p-button-text p-0 mr-3"
          aria-label="Back"
        />
        <h1>{featureTitle}</h1>
      </div>

      {/* Content */}
      <div className="feature-content">
        {/* Feature Information Section */}
        <div className="feature-information-section">
          <div className="section-header">
            <i className="pi pi-info-circle mr-2"></i>
            {featureTitle} Information
          </div>
          <div className="details-grid">
            {(() => {
              const entries = Object.entries(featureDetails);
              const skipKeys = [
                "id",
                "type",
                "category",
                "subCategory",
                "businessHours",
                "paymentMethods",
                "paymentUrls",
                "shopType",
                "createdAt",
                "updatedAt",
                "__v",
              ];

              // Filter out invalid entries
              const validEntries = entries.filter(([key, value]) => {
                if (skipKeys.includes(key)) return false;
                if (!value) return false;
                if (typeof value === "object" && !Array.isArray(value))
                  return false;
                if (Array.isArray(value) && value.length === 0) return false;
                return true;
              });

              // If no valid entries, show message
              if (validEntries.length === 0) {
                return (
                  <div className="no-info-message">
                    <i className="pi pi-info-circle mr-2"></i>
                    No information available or provided
                  </div>
                );
              }

              // Otherwise, render the valid entries
              return validEntries.map(([key, value]) => {
                const displayKey = formatKeyToLabel(key);

                if (typeof value === "boolean") {
                  return (
                    <div key={key} className="detail-item">
                      <div className="detail-label">{displayKey}</div>
                      <div className="detail-value">
                        <i
                          className={`pi ${
                            value
                              ? "pi-check-circle text-green-500"
                              : "pi-times-circle text-red-500"
                          }`}
                        ></i>
                        {value ? " Yes" : " No"}
                      </div>
                    </div>
                  );
                }

                if (Array.isArray(value)) {
                  return (
                    <div key={key} className="detail-item">
                      <div className="detail-label">{displayKey}</div>
                      <div className="detail-value">{value.join(", ")}</div>
                    </div>
                  );
                }

                return (
                  <div key={key} className="detail-item">
                    <div className="detail-label">{displayKey}</div>
                    <div className="detail-value">{String(value)}</div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
        {/* Business Hours Section */}
        {featureDetails.businessHours?.days?.some((day) => day.open) && (
          <div className="business-hours-section">
            <div className="section-header">
              <i className="pi pi-clock mr-2"></i>
              Business Hours
            </div>
            <div className="business-hours-grid">
              {formatBusinessHours(featureDetails.businessHours)?.map(
                (day, index) => {
                  const isToday = (currentDayIndex - 1 + 7) % 7 === index; // Adjust for Sunday being 0
                  return (
                    <div
                      key={index}
                      className={classNames("business-hour-item", {
                        today: isToday,
                      })}
                    >
                      <div className="business-hour-main">
                        <span className="business-hour-day">{day.day}</span>
                        <span
                          className={classNames("business-hour-time", {
                            closed: !day.open,
                          })}
                        >
                          {day.hours}
                        </span>
                      </div>
                    </div>
                  );
                }
              )}
            </div>

            {/* Break Time Information */}
            {hasBreakTime && (
              <div className="special-hours-description mb-3">
                <div className="description-header">
                  <i className="pi pi-clock mr-2"></i>
                  Break Time
                </div>
                <div className="description-content">
                  <div className="description-item">
                    {featureDetails.businessHours.breakStart} -{" "}
                    {featureDetails.businessHours.breakEnd}
                  </div>
                </div>
              </div>
            )}

            {/* Special Hours Information */}
            {hasSpecialHoursDescription && (
              <div className="special-hours-description">
                <div className="description-header">
                  <i className="pi pi-info-circle mr-2"></i>
                  Special Hours Information
                </div>
                <div className="description-content">
                  <div className="description-item">
                    {featureDetails.businessHours.specialHoursDescription}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Payment Methods Section */}
        {(paymentMethods.length > 0 || Object.keys(paymentUrls).length > 0) && (
          <div className="payment-methods-section">
            <div className="section-header">
              <i className="pi pi-credit-card mr-2"></i>
              Payment Methods
            </div>

            <div className="space-y-4">
              {paymentMethods.map((method, index) => {
                const methodType = method.type || method;
                const icon = getPaymentMethodIcon(methodType);
                const name = formatKeyToLabel(methodType);
                const url = paymentUrls[methodType];
                const imageData = paymentImages[methodType];
                const imageUrl =
                  imageData?.url ||
                  (typeof imageData === "string" ? imageData : null);
                const isUrl =
                  url &&
                  (url.startsWith("http://") || url.startsWith("https://"));

                return (
                  <div
                    key={index}
                    className="bg-white p-4 rounded-lg border flex items-center"
                  >
                    <div className="w-1/3 flex items-center">
                      <span className="font-medium text-gray-800 text-right pr-4 w-full">
                        {name}
                      </span>
                    </div>

                    <div className="flex-1 flex items-center justify-start">
                      {imageUrl ? (
                        <div
                          className="cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Image
                            src={imageUrl}
                            alt={`${name} payment information`}
                            width="80"
                            preview
                            className="max-h-12"
                            imageStyle={{ objectFit: "contain" }}
                            previewStyle={{
                              maxWidth: "90vw",
                              maxHeight: "90vh",
                            }}
                          />
                        </div>
                      ) : url ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">
                            {isUrl ? (
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {new URL(url).hostname}
                              </a>
                            ) : (
                              url
                            )}
                          </span>
                          <Button
                            icon="pi pi-copy"
                            className="p-button-text p-button-sm p-button-text m-0 p-0 h-6 w-6"
                            tooltip="Copy to clipboard"
                            tooltipOptions={{ position: "top" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(url);
                              // You might want to show a toast notification here
                            }}
                          />
                        </div>
                      ) : methodType.toLowerCase() === "cash" ? null : (
                        <span className="text-sm text-gray-400">
                          No details provided
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Articles Section */}
        {articles && articles.length > 0 && (
          <div className="articles-section">
            <div className="section-header">
              <i className="pi pi-book mr-2"></i>
              Featured Articles
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article, index) => (
                <Card
                  key={index}
                  className="overflow-hidden shadow-1 hover:shadow-4 transition-all transition-duration-300 cursor-pointer"
                  style={{
                    width: "500px",
                    height: "400px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  onClick={() => {
                    setSelectedArticle(article);
                    setArticleDialogVisible(true);
                  }}
                  header={
                    <div
                      style={{
                        width: "100%",
                        height: "200px",
                        overflow: "hidden",
                      }}
                    >
                      {article.thumbnail?.url ? (
                        <img
                          src={article.thumbnail.url}
                          alt={article.thumbnail.alt || article.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            backgroundColor: "#f3f4f6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <i className="pi pi-image text-4xl text-gray-400"></i>
                        </div>
                      )}
                    </div>
                  }
                >
                  <div
                    style={{
                      padding: "1rem",
                      flex: "1",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <h1
                      style={{
                        fontSize: "1.25rem",
                        fontWeight: "bold",
                        marginBottom: "0.5rem",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {article.title}
                    </h1>

                    {article.introduction && (
                      <p
                        style={{
                          color: "#4b5563",
                          fontSize: "0.875rem",
                          marginBottom: "1rem",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {article.introduction.length > 120
                          ? `${article.introduction.substring(0, 120)}...`
                          : article.introduction}
                      </p>
                    )}

                    {article.tags && article.tags.length > 0 && (
                      <div
                        style={{
                          marginTop: "auto",
                          paddingTop: "1rem",
                          borderTop: "1px solid #e5e7eb",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "0.5rem",
                          }}
                        >
                          {article.tags.slice(0, 3).map((tag, tagIndex) => (
                            <Tag key={tagIndex} value={`#${tag}`} rounded />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Article Dialog */}
        <Dialog
          visible={articleDialogVisible}
          showHeader={false}
          onHide={() => setArticleDialogVisible(false)}
          className="feature-details-modal p-dialog"
          style={{
            width: "100vw",
            height: "100dvh",
            margin: 0,
            padding: 0,
            maxHeight: "100dvh",
            borderRadius: 0,
          }}
          contentStyle={{
            height: "100%",
            maxHeight: "100%",
            margin: 0,
            borderRadius: 0,
            overflow: "auto",
          }}
          breakpoints={{ "960px": "100vw" }}
          closable={false}
          modal
          closeOnEscape={true}
          position={"left"}
        >
          {selectedArticle && (
            <div
              style={{
                padding: "1.5rem",
                margin: "0 auto",
                maxWidth: "1200px",
              }}
            >
              {/* Header */}
              <div className="feature-header">
                <Button
                  icon="pi pi-arrow-left"
                  text
                  onClick={() => setArticleDialogVisible(false)}
                  className="p-button-text p-0 mr-3"
                  aria-label="Back"
                />
                <h1>Featured Articles</h1>
              </div>
              <GalleryTemplate article={selectedArticle} />
            </div>
          )}
        </Dialog>
      </div>
    </div>
  );
};

export default FeatureDetailsPage;
